import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toCaseId } from '../../services/advisorApiAdapter';
import { requiredDocumentSlotTotalForCase } from '../../pages/case-detail/requiredDocuments';
import { formatCaseWorkflowStatus } from '../../utils/caseWorkflowStatus';
// import { resolvedAdvisorCaseTypeLabel } from '../../utils/unifiedCaseTypeLabel';

/** Row fills — only incomplete doc rows get warm highlight. */
const DATA_ROW_BG = 'bg-white';
const DATA_ROW_HOVER = 'hover:bg-slate-50/90';
const ROW_BORDER = 'border-b border-slate-200/80';
const TABLE_HEADER_BG = 'bg-slate-50';
const FAMILY_HEADER_BG = `bg-white hover:bg-slate-50`;

/** Match customer name column across the row (size + weight + primary color). */
const ROW_BODY = 'text-sm font-medium text-slate-800';

function StatusDateCell({ value }) {
  if (!value) return <span className="text-sm text-slate-300">—</span>;
  const d = new Date(value);
  return (
    <div className="flex flex-col">
      <span className="text-sm font-semibold text-slate-700">{d.toLocaleDateString()}</span>
      <span className="text-[10px] text-slate-400 font-medium">{d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
    </div>
  );
}

function getDocCounts(c) {
  const submitted = Number(
    c.document_slots_filled_count != null ? c.document_slots_filled_count : c.uploaded_documents_count || 0,
  );
  const required = requiredDocumentSlotTotalForCase(c);
  return { submitted, required };
}

function assignedAdvisorInitial(c) {
  const name = (c.assigned_advisor_name || '').trim();
  return name ? name[0].toUpperCase() : null;
}

function rowNeedsDocs(c) {
  const { submitted, required } = getDocCounts(c);
  return required > 0 && submitted < required;
}

function DocProgress({ submitted, required }) {
  if (!required) return <span className="text-sm font-medium text-slate-500">—</span>;
  return <span className={`${ROW_BODY} whitespace-nowrap`}>{submitted}/{required}</span>;
}

function getStatusBadgeClass(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'new') return 'adv-status-new';
  if (s.includes('progress')) return 'adv-status-progress';
  if (s.includes('attention') || s.includes('waiting') || s.includes('docs')) return 'adv-status-attention';
  if (s.includes('review')) return 'adv-status-review';
  if (s.includes('approved')) return 'adv-status-progress';
  return 'adv-status-default';
}

function StatusBadge({ status }) {
  const { t } = useTranslation();
  return (
    <span className={`adv-status-badge ${getStatusBadgeClass(status)}`}>
      {formatCaseWorkflowStatus(status)}
    </span>
  );
}

function sortFamilyRows(rows) {
  const arr = [...rows];
  arr.sort((a, b) => {
    if (a.is_lead_contact && !b.is_lead_contact) return -1;
    if (!a.is_lead_contact && b.is_lead_contact) return 1;
    const fa = String(a.user_first_name || '').toLowerCase();
    const fb = String(b.user_first_name || '').toLowerCase();
    if (fa !== fb) return fa.localeCompare(fb);
    const la = String(a.user_last_name || '').toLowerCase();
    const lb = String(b.user_last_name || '').toLowerCase();
    if (la !== lb) return la.localeCompare(lb);
    const na = String(a.user_name || '').toLowerCase();
    const nb = String(b.user_name || '').toLowerCase();
    return na.localeCompare(nb);
  });
  return arr;
}

function withListReturn(state, listReturnTo) {
  if (!listReturnTo) return state;
  return { ...state, returnTo: listReturnTo };
}

function FamilyRows({ familyName, rows, advisorName, listReturnTo }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const navigate = useNavigate();
  const sortedRows = useMemo(() => sortFamilyRows(rows), [rows]);
  return (
    <>
      <tr
        className={`border-b border-[#D8D4CE] cursor-pointer transition-colors ${FAMILY_HEADER_BG}`}
        onClick={() => setExpanded(!expanded)}
      >
        <td colSpan={1} className="px-3 py-3 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-semibold text-slate-800 break-words min-w-0">
              {t('advisorPortal.familyLine', { name: familyName })}
            </span>
            <i className={`ri-arrow-${expanded ? 'up' : 'down'}-s-line text-slate-400 text-sm leading-none shrink-0`} />
          </div>
        </td>
        <td className="px-3 py-3" />
        <td className="px-3 py-3" />
        <td className="px-3 py-3" />
        <td className="px-3 py-3" />
        <td className="px-3 py-3" />
        <td className="px-3 py-3" />
      </tr>
      {expanded && sortedRows.map((c) => (
        <tr
          key={`${c.user_id}-${c.session_id}`}
          className={`${ROW_BORDER} transition-colors ${rowNeedsDocs(c) ? 'bg-[#FBF8F1] hover:bg-[#F5F1E8]' : `${DATA_ROW_BG} ${DATA_ROW_HOVER}`}`}
        >
          <td className="py-2.5 pl-3 pr-3 align-top">
            <div className="flex items-stretch min-h-[2.75rem]">
              {/* ~8px after cell pl-3 → strip starts ~20px from column edge (Reddy gutter) */}
              <div className="w-2 shrink-0" aria-hidden />
              <div className="w-[3px] shrink-0 bg-teal-500 self-stretch rounded-[1px]" aria-hidden />
              {/* ~16px between strip and name */}
              <div className="flex min-w-0 flex-1 items-center pl-4 gap-2">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/customer/${c.user_id}`, {
                      state: withListReturn({
                        userName: c.user_name,
                        userId: c.user_id,
                        advisorName,
                      }, listReturnTo),
                    });
                  }}
                  className={`${ROW_BODY} text-left break-words sm:whitespace-nowrap hover:text-slate-600 transition-colors cursor-pointer min-h-[44px] sm:min-h-0 inline-flex items-center touch-manipulation`}
                >
                  {c.user_name || '—'}
                </button>
              </div>
            </div>
          </td>
          {/*
          <td className="px-3 py-2.5 min-w-0 max-w-[11rem] md:max-w-none">
            <span className={`${ROW_BODY} break-words md:whitespace-nowrap`}>
              {resolvedAdvisorCaseTypeLabel(c.form_name, t)}
            </span>
          </td>
          */}
          <td className="pl-3 pr-1 py-2.5 min-w-0">
            <p className={`${ROW_BODY} whitespace-nowrap`}>{c.user_phone || '—'}</p>
            <p className={`${ROW_BODY} break-all line-clamp-2 md:truncate md:max-w-[200px]`}>{c.user_email || '—'}</p>
          </td>
          <td className="pl-1 pr-3 py-2.5">
            <span className={`${ROW_BODY} whitespace-nowrap`}>{formatCaseWorkflowStatus(c.status)}</span>
          </td>
          <td className="px-3 py-2.5">
            <StatusDateCell value={c.status_updated_at} />
          </td>
          <td className="px-3 py-2.5">
            <span className={`${ROW_BODY} whitespace-nowrap`}>
              {(c.assigned_advisor_name || t('advisorPortal.unassigned')).split(' ')[0]}
            </span>
          </td>
          <td className="px-3 py-2.5">
            <DocProgress
              submitted={getDocCounts(c).submitted}
              required={getDocCounts(c).required}
            />
          </td>
          <td className="px-3 py-2.5">
            <button
              type="button"
              className={`inline-flex min-h-[40px] items-center justify-center gap-1 ${ROW_BODY} border border-slate-300 px-3 py-2 sm:min-h-0 sm:px-2.5 sm:py-1 rounded-md hover:bg-slate-50 transition-colors cursor-pointer touch-manipulation whitespace-nowrap`}
              onClick={() => navigate(`/case/${toCaseId(c.user_id, c.session_id, c.public_case_id)}`, {
                state: withListReturn({
                  userName: c.user_name,
                  publicCaseId: c.public_case_id,
                  userId: c.user_id,
                  sessionId: c.session_id,
                  advisorName,
                }, listReturnTo),
              })}
            >
              {t('advisorPortal.tableOpen')}
            </button>
          </td>
        </tr>
      ))}
      <tr aria-hidden="true">
        <td colSpan={7} className="p-0 border-0 h-2.5 bg-[#EDE9E3]/80" />
      </tr>
    </>
  );
}

function MobileCaseCard({ c, advisorName, listReturnTo }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const docs = getDocCounts(c);
  const needsAttention = rowNeedsDocs(c);

  return (
    <article
      className="adv-modern-card adv-animate-fade-in flex flex-col mb-4 mx-1"
    >
      {needsAttention && <div className="adv-card-accent bg-amber-400" />}
      <div className="p-4">
        <div className="flex justify-between items-start gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => navigate(`/customer/${c.user_id}`, {
                  state: { userName: c.user_name, userId: c.user_id, advisorName }
                })}
                className="text-base font-bold text-slate-900 text-left hover:text-emerald-600 transition-colors block truncate"
              >
                {c.user_name || '—'}
              </button>
              <StatusBadge status={c.status} />
            </div>

            {/* Shifted up mobile / email */}
            <div className="mt-2 space-y-1">
              <p className="text-xs text-slate-700 font-medium truncate flex items-center gap-1.5">
                <i className="ri-phone-line text-slate-300" /> {c.user_phone || '—'}
              </p>
              <p className="text-xs text-slate-500 truncate flex items-center gap-1.5">
                <i className="ri-mail-line text-slate-300" /> {c.user_email || '—'}
              </p>
            </div>
          </div>

          <div className="shrink-0 flex flex-col items-end gap-3.5">
            {/* Docs Progress */}
            <div className="text-right">
              <span className="block text-[10px] text-slate-400 font-medium uppercase tracking-widest mb-1">{t('advisorPortal.tableDocs')}</span>
              <div className="text-sm font-bold text-slate-800">
                {docs.required ? (
                  <div className="flex items-center gap-1.5">
                    <div className="w-12 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${docs.submitted >= docs.required ? 'bg-emerald-500' : 'bg-amber-400'}`}
                        style={{ width: `${Math.min(100, (docs.submitted / docs.required) * 100)}%` }}
                      />
                    </div>
                    <span className="tabular-nums text-xs">{docs.submitted}/{docs.required}</span>
                  </div>
                ) : <span className="text-slate-300">—</span>}
              </div>
            </div>

            {/* Open Button replacing Advisor Button */}
            <button
              type="button"
              className="inline-flex h-9 items-center justify-center gap-1.5 bg-slate-900 text-white rounded-lg px-3 text-xs font-bold hover:bg-slate-800 active:scale-[0.98] transition-all shadow-sm"
              onClick={() => navigate(`/case/${toCaseId(c.user_id, c.session_id, c.public_case_id)}`, {
                state: {
                  userName: c.user_name,
                  publicCaseId: c.public_case_id,
                  userId: c.user_id,
                  sessionId: c.session_id,
                  advisorName
                }
              })}
            >
              <i className="ri-external-link-line" /> {t('advisorPortal.tableOpen')}
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}


function MobileFamilyRows({ familyName, rows, advisorName }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const sortedRows = useMemo(() => sortFamilyRows(rows), [rows]);

  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <button
        type="button"
        className={`flex w-full min-w-0 items-center justify-between gap-2 border-b border-[#D8D4CE] px-3 py-3 text-left transition-colors touch-manipulation ${FAMILY_HEADER_BG}`}
        onClick={() => setExpanded(!expanded)}
      >
        <span className="min-w-0 truncate text-sm font-semibold text-slate-800">
          {t('advisorPortal.familyLine', { name: familyName })}
        </span>
        <i className={`ri-arrow-${expanded ? 'up' : 'down'}-s-line text-slate-400 text-sm leading-none shrink-0`} />
      </button>
      {expanded ? sortedRows.map((c) => (
        <MobileCaseCard key={`${c.user_id}-${c.session_id}`} c={c} advisorName={advisorName} listReturnTo={listReturnTo} />
      )) : null}
    </section>
  );
}

export default function AdvisorApplicationsTable({ cases, advisorName, listReturnTo }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const grouped = useMemo(() => {
    const fam = new Map(); // groupId -> { familyLabel, leadContactId, rows }
    const solo = [];
    cases.forEach((c) => {
      const fgId = c.family_group_id;
      if (!fgId) return solo.push(c);

      const key = String(fgId);
      const prev = fam.get(key);
      if (prev) {
        prev.rows.push(c);
      } else {
        fam.set(key, {
          familyLabel: c.family_name || t('advisorPortal.defaultFamilyName'),
          rows: [c],
        });
      }
    });
    return { fam, solo };
  }, [cases, t]);

  if (!cases.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl py-16 text-center text-slate-400 text-sm">
        <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 opacity-40">
          <i className="ri-search-line text-3xl" />
        </div>
        {t('advisorPortal.noApplicationsFound')}
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6 sm:hidden pb-10">
        {Array.from(grouped.fam.entries()).map(([groupId, fg]) => (
          <div key={groupId} className="space-y-3">
            <div className="px-2 flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                <i className="ri-group-line text-emerald-600" /> {t('advisorPortal.familyLine', { name: fg.familyLabel })}
              </h3>
              <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full uppercase tracking-widest">{fg.rows.length} {t('advisorPortal.members', { defaultValue: 'Members' })}</span>
            </div>
            {sortFamilyRows(fg.rows).map((c) => (
              <MobileCaseCard key={`${c.user_id}-${c.session_id}`} c={c} advisorName={advisorName} listReturnTo={listReturnTo} />
            ))}
          </div>
        ))}
        {grouped.solo.length ? (
          <div className="space-y-3">
            {grouped.solo.map((c) => (
              <MobileCaseCard key={`${c.user_id}-${c.session_id}`} c={c} advisorName={advisorName} listReturnTo={listReturnTo} />
            ))}
          </div>
        ) : null}
      </div>

      <div className="hidden w-full max-w-full min-w-0 overflow-x-auto overscroll-x-contain touch-pan-x rounded-xl border border-slate-200 sm:block">
        <table className="w-full min-w-[900px]">
          <thead>
            <tr className={`${TABLE_HEADER_BG} border-b border-[#D8D4CE]`}>
              <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">{t('advisorPortal.tableCustomer')}</th>
              {/*
            <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">{t('advisorPortal.tableCaseType')}</th>
            */}
              <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('advisorPortal.tableContact')}</th>
              <th className="w-36 px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('advisorPortal.tableStatus')}</th>
              <th className="w-40 px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('advisorPortal.tableStatusDate')}</th>
              <th className="w-24 px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('advisorPortal.tableAdvisor')}</th>
              <th className="w-28 px-3 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('advisorPortal.tableDocs')}</th>
              <th className="w-28 px-3 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide" />
            </tr>
          </thead>
          <tbody>
            {Array.from(grouped.fam.entries()).map(([groupId, fg]) => (
              <FamilyRows
                key={groupId}
                familyName={fg.familyLabel}
                rows={fg.rows}
                advisorName={advisorName}
                listReturnTo={listReturnTo}
              />
            ))}
            {grouped.solo.map((c) => (
              <tr
                key={`${c.user_id}-${c.session_id}`}
                className={`${ROW_BORDER} transition-colors ${rowNeedsDocs(c) ? 'bg-[#FBF8F1] hover:bg-[#F5F1E8]' : `${DATA_ROW_BG} ${DATA_ROW_HOVER}`}`}
              >
                <td className="px-3 py-3">
                  <button
                    type="button"
                    onClick={() => navigate(`/customer/${c.user_id}`, {
                      state: withListReturn({
                        userName: c.user_name,
                        userId: c.user_id,
                        advisorName,
                      }, listReturnTo),
                    })}
                    className={`${ROW_BODY} text-left break-words sm:whitespace-nowrap hover:text-slate-600 transition-colors cursor-pointer min-h-[44px] sm:min-h-0 inline-flex items-center touch-manipulation max-w-full`}
                  >
                    {c.user_name || '—'}
                  </button>
                </td>
                {/*
              <td className="px-3 py-3 min-w-0 max-w-[10rem] sm:max-w-none"><span className={`${ROW_BODY} break-words sm:whitespace-nowrap`}>{resolvedAdvisorCaseTypeLabel(c.form_name, t)}</span></td>
              */}
                <td className="pl-3 pr-1 py-3 min-w-0">
                  <p className={`${ROW_BODY} whitespace-nowrap`}>{c.user_phone || '—'}</p>
                  <p className={`${ROW_BODY} break-all line-clamp-2 sm:truncate sm:max-w-[200px]`}>{c.user_email || '—'}</p>
                </td>
                <td className="pl-1 pr-3 py-4">
                  <StatusBadge status={c.status} />
                </td>
                <td className="px-3 py-4">
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold text-slate-700">{new Date(c.status_updated_at).toLocaleDateString()}</span>
                    <span className="text-[10px] text-slate-400 font-medium">{new Date(c.status_updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                </td>
                <td className="px-3 py-4">
                  <div className="flex items-center gap-2">
                    {assignedAdvisorInitial(c) ? (
                      <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-[11px] font-bold text-slate-500 border border-slate-200">
                        {assignedAdvisorInitial(c)}
                      </div>
                    ) : null}
                    <span className="text-sm font-medium text-slate-700">
                      {(c.assigned_advisor_name || t('advisorPortal.unassigned')).split(' ')[0]}
                    </span>
                  </div>
                </td>
                <td className="px-3 py-4">
                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-bold text-slate-800">{getDocCounts(c).submitted}/{getDocCounts(c).required}</span>
                      <span className="text-[10px] font-bold text-slate-400">{Math.round((getDocCounts(c).submitted / (getDocCounts(c).required || 1)) * 100)}%</span>
                    </div>
                    <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${getDocCounts(c).submitted >= getDocCounts(c).required ? 'bg-emerald-500' : 'bg-amber-400'}`}
                        style={{ width: `${Math.min(100, (getDocCounts(c).submitted / (getDocCounts(c).required || 1)) * 100)}%` }}
                      />
                    </div>
                  </div>
                </td>
                <td className="px-3 py-4 text-center">
                  <button
                    type="button"
                    className="inline-flex h-9 items-center justify-center gap-2 bg-white border border-slate-200 rounded-lg px-4 text-sm font-bold text-slate-700 hover:bg-slate-50 hover:border-slate-300 hover:text-slate-900 transition-all active:scale-95 shadow-sm"
                    onClick={() => navigate(`/case/${toCaseId(c.user_id, c.session_id, c.public_case_id)}`, {
                      state: withListReturn({
                        userName: c.user_name,
                        publicCaseId: c.public_case_id,
                        userId: c.user_id,
                        sessionId: c.session_id,
                        advisorName,
                      }, listReturnTo),
                    })}
                  >
                    <i className="ri-external-link-line text-slate-400" /> {t('advisorPortal.tableOpen')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </>
  );
}

